import type { ReactNode } from 'react';
import React, { useEffect, useReducer } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoadTerms } from '../components/atoms/LoadTerms/LoadTerms';
import { CheckAuth } from '../components/molecules/CheckAuth/CheckAuth';
import { PrivateRoute } from '../components/molecules/PrivateRoute/PrivateRoute';
import { Appointment } from '../components/pages/Appointment/Appointment';
import { UnavailableAppointment } from '../components/pages/Appointment/UnavailableAppointment';
import { AppointmentChoice } from '../components/pages/AppointmentChoice/AppointmentChoice';
import { AppointmentRoom } from '../components/pages/AppointmentRoom/AppointmentRoom';
import { CertificateRequestStatus } from '../components/pages/CertificateRequestStatus/CertificateRequestStatus';
import { Certificates } from '../components/pages/Certificates/Certificates';
import { IDCardCheckout } from '../components/pages/Checkouts/IDCardCheckout';
import { MedicalCertificateCheckout } from '../components/pages/Checkouts/MedicalCertificateCheckout';
import { MedicalReportCheckout } from '../components/pages/Checkouts/MedicalReportCheckout';
import { SchengenCertificateCheckout } from '../components/pages/Checkouts/SchengenCertificateCheckout';
import { VehicleCertificateCheckout } from '../components/pages/Checkouts/VehicleCertificateCheckout';
import { ConfirmTerms } from '../components/pages/ConfirmTerms/ConfirmTerms';
import { Contact } from '../components/pages/Contact/Contact';
import { Dashboard } from '../components/pages/Dashboard/Dashboard';
import { Documents } from '../components/pages/Documents/Documents';
import { ErrorPage } from '../components/pages/Error/ErrorPage';
import { FollowUpPrescription } from '../components/pages/FollowUpPrescription/FollowUpPrescription';
import { ForgotPassword } from '../components/pages/ForgotPassword/ForgotPassword';
import { Login } from '../components/pages/Login/Login';
import { MyAccount } from '../components/pages/MyAccount/MyAccount';
import { MyPharmacy } from '../components/pages/MyPharmacy/MyPharmacy';
import { PharmacyDetails } from '../components/pages/MyPharmacy/PharmacyDetails';
import { PharmacyList } from '../components/pages/MyPharmacy/PharmacyList';
import { PageNotFound } from '../components/pages/PageNotFound/PageNotFound';
import { PatientenTracking } from '../components/pages/PatientenTracking/PatientenTracking';
import { TrackingSuccess } from '../components/pages/PatientenTracking/TrackingSuccess';
import { PaymentRedirect } from '../components/pages/PaymentRedirect/PaymentRedirect';
import { Payments } from '../components/pages/Payments/Payments';
import { PaymentStatusPage } from '../components/pages/PaymentStatusPage/PaymentStatusPage';
import { PrivacyPolicy } from '../components/pages/PrivacyPolicy/PrivacyPolicy';
import { Questionnaire } from '../components/pages/Questionnaire/Questionnaire';
import { RegisterProcedure } from '../components/pages/Register/RegisterProcedure/RegisterProcedure';
import { RegisterSuccess } from '../components/pages/Register/Success/RegisterSuccess';
import { ResetPassword } from '../components/pages/ResetPassword/ResetPassword';
import { SetPassword } from '../components/pages/SetPassword/SetPassword';
import { TermsAndConditions } from '../components/pages/TermsAndConditions/TermsAndConditions';
import { UpdatePersonalData } from '../components/pages/UpdatePersonalData/UpdatePersonalData';
import { initialState, reactReducer } from '../helper/NavigationReducer';

type RouterProps = {
  children?: ReactNode;
};

export function Router({ children }: RouterProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [state, reactDispatch] = useReducer(reactReducer, initialState);

  function ScrollUp(): JSX.Element {
    const location = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [location]);

    return <React.Fragment></React.Fragment>;
  }

  return (
    <BrowserRouter>
      <ScrollUp />
      {children}
      <LoadTerms />
      <Routes>
        <Route path='/' element={<Navigate to='/dashboard' />} />
        <Route
          path='/dashboard'
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route path='/login' element={<Login />} />
        <Route path='/register' element={<RegisterProcedure />} />
        <Route path='/register/success' element={<RegisterSuccess />} />
        <Route
          path='/my-account'
          element={
            <PrivateRoute>
              <MyAccount />
            </PrivateRoute>
          }
        />
        <Route
          path='/payments'
          element={
            <PrivateRoute>
              <Payments />
            </PrivateRoute>
          }
        />
        <Route
          path='/update-personal-data'
          element={
            <PrivateRoute>
              <UpdatePersonalData />
            </PrivateRoute>
          }
        />
        <Route
          path='/questionnaire'
          element={
            <PrivateRoute>
              <Questionnaire />
            </PrivateRoute>
          }
        />
        <Route
          path='/patienten-tracking'
          element={
            <CheckAuth>
              <PatientenTracking />
            </CheckAuth>
          }
        />
        <Route
          path='/patienten-tracking/success'
          element={
            <CheckAuth>
              <TrackingSuccess />
            </CheckAuth>
          }
        />
        <Route
          path='/my-pharmacy'
          element={
            <PrivateRoute>
              <MyPharmacy />
            </PrivateRoute>
          }
        />
        <Route
          path='/pharmacy-list'
          element={
            <PrivateRoute>
              <PharmacyList setCurrentPage={reactDispatch} />
            </PrivateRoute>
          }
        />
        <Route
          path='/pharmacy-details/:pharmacyId'
          element={
            <PrivateRoute>
              <PharmacyDetails setCurrentPage={reactDispatch} />
            </PrivateRoute>
          }
        />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        <Route path='/set-password' element={<SetPassword />} />
        <Route
          path='/profile/documents'
          element={
            <PrivateRoute>
              <Documents />
            </PrivateRoute>
          }
        />
        <Route
          path='/terms-and-conditions'
          element={
            <PrivateRoute>
              <TermsAndConditions />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile/appointment-choice'
          element={
            <PrivateRoute requiresActivePatient>
              <AppointmentChoice />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile/online-appointment'
          element={
            <PrivateRoute requiresActivePatient>
              <Appointment
                isFirstAppointment={false}
                isOffline={false}
                setCurrentPage={reactDispatch}
                currentPage={state.appointmentIndex}
              />
            </PrivateRoute>
          }
        />
        <Route
          path='/unavailable-appointment'
          element={
            <PrivateRoute requiresActivePatient>
              <UnavailableAppointment />
            </PrivateRoute>
          }
        />
        <Route
          path='/appointment/:appointmentId'
          element={
            <PrivateRoute requiresActivePatient>
              <AppointmentRoom />
            </PrivateRoute>
          }
        />
        <Route
          path='/privacy-policy'
          element={
            <PrivateRoute>
              <PrivacyPolicy />
            </PrivateRoute>
          }
        />
        <Route
          path='/confirm-terms'
          element={
            <PrivateRoute>
              <ConfirmTerms />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile/online-first-appointment'
          element={
            <PrivateRoute requiresActivePatient>
              <Appointment
                isFirstAppointment={true}
                isOffline={false}
                setCurrentPage={reactDispatch}
                currentPage={state.appointmentIndex}
              />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile/offline-first-appointment'
          element={
            <PrivateRoute requiresActivePatient>
              <Appointment
                isFirstAppointment={true}
                isOffline={true}
                setCurrentPage={reactDispatch}
                currentPage={state.appointmentIndex}
              />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile/offline-appointment'
          element={
            <PrivateRoute requiresActivePatient>
              <Appointment
                isFirstAppointment={false}
                isOffline={true}
                setCurrentPage={reactDispatch}
                currentPage={state.appointmentIndex}
              />
            </PrivateRoute>
          }
        />
        <Route
          path='/follow-up-prescription'
          element={
            <PrivateRoute requiresActivePatient>
              <FollowUpPrescription currentPage={state.followUpPrescriptionsIndex} setCurrentPage={reactDispatch} />
            </PrivateRoute>
          }
        />
        <Route
          path='/contact'
          element={
            <PrivateRoute>
              <Contact />
            </PrivateRoute>
          }
        />
        <Route
          path='/error'
          element={
            <CheckAuth>
              <ErrorPage />
            </CheckAuth>
          }
        />
        <Route
          path='/id-card'
          element={
            <PrivateRoute requiresActivePatient>
              <IDCardCheckout />
            </PrivateRoute>
          }
        />
        <Route
          path='/schengen-certificate'
          element={
            <PrivateRoute requiresActivePatient>
              <SchengenCertificateCheckout />
            </PrivateRoute>
          }
        />
        <Route
          path='/vehicle-certificate'
          element={
            <PrivateRoute requiresActivePatient>
              <VehicleCertificateCheckout />
            </PrivateRoute>
          }
        />
        <Route
          path='/medical-certificate'
          element={
            <PrivateRoute requiresActivePatient>
              <MedicalCertificateCheckout />
            </PrivateRoute>
          }
        />
        <Route
          path='/medical-report'
          element={
            <PrivateRoute requiresActivePatient>
              <MedicalReportCheckout />
            </PrivateRoute>
          }
        />
        <Route
          path='/payment-status'
          element={
            <PrivateRoute>
              <PaymentStatusPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/certificates'
          element={
            <PrivateRoute requiresActivePatient>
              <Certificates />
            </PrivateRoute>
          }
        />
        <Route
          path='/certificate-request-status'
          element={
            <PrivateRoute requiresActivePatient>
              <CertificateRequestStatus />
            </PrivateRoute>
          }
        />
        <Route path='/payment-redirect' element={<PaymentRedirect />} />
        <Route
          path='*'
          element={
            <PrivateRoute>
              <PageNotFound />
            </PrivateRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
